import { useState, useRef, useEffect, useCallback } from 'react';
import { CFormInput, CInputGroup, CInputGroupText } from '@coreui/react-pro';
import { PaperPlaneTiltIcon, XIcon, ChatCircleIcon, ArrowClockwiseIcon, TrashIcon } from '@phosphor-icons/react';
import { useSelector } from 'react-redux';
import { chatbot } from 'src/services/ai';
import { isAiEnabled } from 'src/utils/rights';

const SUGGESTED_QUESTIONS = [
	'What is phishing?',
	'How does phishing work?',
	'How to identify phishing emails?',
	'What should I do if I click a phishing link?',
	'What are common phishing techniques?',
];

const MAX_CHARS = 500;

/* Portal brand red (matches primary-btn / custom-red-btn across the app) */
// Identity red (#DD3731) is never used on a control per the UEMP token spec —
// use the desaturated --app-accent-fill token for solid fills instead.
const BRAND = 'var(--app-accent-fill)';

const INITIAL_MESSAGE = {
	role: 'bot',
	text: "Hello! I'm your AI Security Assistant. Ask me anything about phishing, email threats, or online safety — or pick a suggested question below.",
	time: new Date(),
};

/* ── Render bot message with basic markdown ── */
const MessageContent = ({ text }) => {
	const lines = (text || '').split('\n');
	return (
		<>
			{lines.map((line, i) => {
				const trimmed = line.trim();
				if (!trimmed) return <br key={i} />;

				const isBullet = /^[-*•]\s/.test(trimmed);
				const content = isBullet ? trimmed.replace(/^[-*•]\s/, '') : trimmed;

				const renderInline = (str) =>
					str.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((part, j) => {
						if (part.startsWith('**') && part.endsWith('**'))
							return <strong key={j}>{part.slice(2, -2)}</strong>;
						if (part.startsWith('`') && part.endsWith('`'))
							return (
								<code
									key={j}
									style={{
										background: 'rgba(127,127,127,0.18)',
										borderRadius: 3, color: 'var(--app-text)',
										padding: '1px 5px',
										fontSize: '0.83em',
										fontFamily: 'monospace',
									}}
								>
									{part.slice(1, -1)}
								</code>
							);
						return <span key={j}>{part}</span>;
					});

				return (
					<div key={i} style={{ display: 'flex', gap: 6, marginTop: i === 0 ? 0 : 3 }}>
						{isBullet && <span style={{ minWidth: 12, opacity: 0.7 }}>•</span>}
						<span>{renderInline(content)}</span>
					</div>
				);
			})}
		</>
	);
};

/* ── Animated typing dots ── */
const TypingDots = () => (
	<div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 2px' }}>
		{[0, 1, 2].map((i) => (
			<span
				key={i}
				style={{
					width: 7,
					height: 7,
					borderRadius: '50%',
					background: 'var(--app-text-muted)',
					display: 'inline-block',
					animation: `cbBounce 1.3s ${i * 0.22}s infinite ease-in-out`,
				}}
			/>
		))}
	</div>
);

/* ── Time formatter ── */
const fmtTime = (d) =>
	d instanceof Date
		? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
		: '';

/* ── Main component ── */
const Chatbot = () => {
	const user = useSelector((state) => state.user?.userData?.user);
	const currentCustomer = useSelector((state) => state.customer?.currentCustomer);

	const canShow = isAiEnabled(user, currentCustomer);

	const [open, setOpen] = useState(false);
	const [messages, setMessages] = useState([INITIAL_MESSAGE]);
	const [input, setInput] = useState('');
	const [loading, setLoading] = useState(false);
	const [lastError, setLastError] = useState(null);
	const endRef = useRef(null);
	const inputRef = useRef(null);

	useEffect(() => {
		if (open) {
			endRef.current?.scrollIntoView({ behavior: 'smooth' });
			setTimeout(() => inputRef.current?.focus(), 80);
		}
	}, [messages, open]);

	const sendMessage = useCallback(
		async (text) => {
			const trimmed = (text || input).trim();
			if (!trimmed || loading) return;
			if (trimmed.length > MAX_CHARS) return;

			setLastError(null);
			const now = new Date();
			setMessages((prev) => [...prev, { role: 'user', text: trimmed, time: now }]);
			setInput('');
			setLoading(true);

			try {
				const reply = await chatbot(trimmed, currentCustomer?._id);
				setMessages((prev) => [...prev, { role: 'bot', text: reply, time: new Date() }]);
			} catch (err) {
				const errText = err.message || 'Something went wrong. Please try again.';
				const lower = errText.toLowerCase();
				const isDisabled = lower.includes('disabled by your administrator');
				const isNotConfigured =
					!isDisabled && (lower.includes('not configured') ||
					lower.includes('not set up'));
				const isQuota =
					lower.includes('quota') ||
					lower.includes('rate limit') ||
					lower.includes('billing');
				const isModelError =
					lower.includes('model name') ||
					lower.includes('model format') ||
					lower.includes('unexpected model');
				// quota + model errors: no point retrying
				const errorType = (isDisabled || isNotConfigured || isQuota || isModelError) ? 'not_configured' : 'generic';
				setLastError(errorType);
				setMessages((prev) => [
					...prev,
					{
						role: 'bot',
						text: isDisabled
							? '🚫  The AI Assistant has been disabled by your administrator. Please contact your admin for assistance.'
							: isNotConfigured
							? '⚙️  AI is not configured yet. Please ask your administrator to set up an AI provider in Settings → AI Configuration.'
							: errText,
						isError: true,
						time: new Date(),
					},
				]);
			} finally {
				setLoading(false);
			}
		},
		[input, loading, currentCustomer?._id]
	);

	const handleKeyDown = (e) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			sendMessage();
		}
	};

	const clearConversation = () => {
		setMessages([INITIAL_MESSAGE]);
		setLastError(null);
		setInput('');
	};

	const retryLast = () => {
		const userMessages = messages.filter((m) => m.role === 'user');
		if (!userMessages.length) return;
		const last = userMessages[userMessages.length - 1].text;
		// Remove last bot error message
		setMessages((prev) => prev.slice(0, -1));
		sendMessage(last);
	};

	const showSuggestions = messages.length <= 1;
	const charCount = input.length;
	const overLimit = charCount > MAX_CHARS;

	if (!canShow) return null;

	return (
		<>
			<style>{`
				@keyframes cbBounce {
					0%,80%,100%{transform:scale(.55);opacity:.45}
					40%{transform:scale(1);opacity:1}
				}
				.cb-msg-user { word-break:break-word; white-space:pre-wrap; }
				.cb-msg-bot  { word-break:break-word; white-space:pre-wrap; }
				.cb-suggest-btn:hover { background:${BRAND}!important;color:#fff!important;border-color:${BRAND}!important;filter:brightness(0.88); }
				.cb-close-btn:hover { opacity:1!important; }
				.cb-action-btn:hover { opacity:1!important; background:rgba(255,255,255,.15)!important; }
				.cb-toggle-btn:hover { background:${BRAND}!important;filter:brightness(0.88); }
			`}</style>

			{/* ── Floating toggle button ── */}
			<button
				className="cb-toggle-btn"
				onClick={() => setOpen((v) => !v)}
				title={open ? 'Close assistant' : 'Open AI Assistant'}
				style={{
					position: 'fixed',
					bottom: '1.5rem',
					right: '1.5rem',
					width: 52,
					height: 52,
					borderRadius: '50%',
					background: BRAND,
					border: 'none',
					color: '#fff',
					cursor: 'pointer',
					// Below every modal/offcanvas layer (1040-1055) so open dialogs cover
					// and disable it, same fix as the sidebar floating toggle.
					zIndex: 1030,
					boxShadow: '0 2px 10px rgba(221,55,49,.35)',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					transition: 'background 0.15s, transform 0.15s',
				}}
			>
				{open ? <XIcon weight="bold" size={20} /> : <ChatCircleIcon weight="bold" size={20} />}
			</button>

			{/* ── Chat window ── */}
			{open && (
				<div
					style={{
						position: 'fixed',
						bottom: '5rem',
						right: '1.5rem',
						width: 380,
						maxHeight: 560,
						background: 'var(--app-card-bg)',
						borderRadius: '14px',
						boxShadow: '0 12px 40px rgba(0,0,0,.2)',
						display: 'flex',
						flexDirection: 'column',
						zIndex: 1029,
						overflow: 'hidden',
						border: '1px solid var(--app-border)',
					}}
				>
					{/* Header */}
					<div
						style={{
							background: BRAND,
							color: '#fff',
							padding: '0.8rem 1rem',
							display: 'flex',
							alignItems: 'center',
							gap: '0.6rem',
							flexShrink: 0,
						}}
					>
						<div
							style={{
								width: 36,
								height: 36,
								borderRadius: '50%',
								background: 'rgba(255,255,255,0.18)',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								flexShrink: 0,
							}}
						>
							<ChatCircleIcon weight="bold" size={20} />
						</div>
						<div style={{ flex: 1, minWidth: 0 }}>
							<div style={{ fontWeight: 700, fontSize: '0.92rem', lineHeight: 1.2 }}>
								AI Security Assistant
							</div>
							<div style={{ fontSize: '0.7rem', opacity: 0.8, marginTop: 1 }}>
								Cybersecurity guidance & phishing awareness
							</div>
						</div>
						{/* Clear conversation */}
						<button
							className="cb-action-btn"
							onClick={clearConversation}
							title="Clear conversation"
							disabled={messages.length <= 1}
							style={{
								background: 'none',
								border: 'none',
								color: '#fff',
								cursor: messages.length <= 1 ? 'not-allowed' : 'pointer',
								opacity: messages.length <= 1 ? 0.4 : 0.7,
								padding: '4px 5px',
								borderRadius: 6,
								transition: 'opacity 0.15s, background 0.15s',
								display: 'flex',
								alignItems: 'center',
							}}
						>
							<TrashIcon weight="bold" size={16} />
						</button>
						{/* Close */}
						<button
							className="cb-close-btn"
							onClick={() => setOpen(false)}
							title="Close"
							style={{
								background: 'none',
								border: 'none',
								color: '#fff',
								cursor: 'pointer',
								opacity: 0.7,
								padding: '4px 5px',
								borderRadius: 6,
								transition: 'opacity 0.15s',
								display: 'flex',
								alignItems: 'center',
							}}
						>
							<XIcon weight="bold" size={16} />
						</button>
					</div>

					{/* Messages area */}
					<div
						style={{
							flex: 1,
							overflowY: 'auto',
							padding: '1rem',
							display: 'flex',
							flexDirection: 'column',
							gap: '0.65rem',
							background: 'var(--app-bg)',
						}}
					>
						{messages.map((msg, idx) => (
							<div
								key={idx}
								style={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
									maxWidth: '100%',
								}}
							>
								<div
									className={msg.role === 'user' ? 'cb-msg-user' : 'cb-msg-bot'}
									style={{
										maxWidth: '84%',
										background: msg.isError
											? 'var(--app-badge-readonly-bg)'
											: msg.role === 'user'
											? BRAND
											: 'var(--app-card-bg)',
										color: msg.isError
											? 'var(--app-badge-readonly-text)'
											: msg.role === 'user'
											? '#fff'
											: 'var(--app-text)',
										borderRadius:
											msg.role === 'user'
												? '1.1rem 1.1rem 0.2rem 1.1rem'
												: '1.1rem 1.1rem 1.1rem 0.2rem',
										padding: '0.65rem 0.9rem',
										fontSize: '0.875rem',
										lineHeight: 1.55,
										boxShadow: msg.role === 'user'
											? '0 2px 8px rgba(221,55,49,.25)'
											: '0 1px 5px rgba(0,0,0,.08)',
										border: msg.isError ? '1px solid var(--app-badge-readonly-border)' : 'none',
									}}
								>
									<MessageContent text={msg.text} />
								</div>
								<div
									style={{
										fontSize: '0.65rem',
										color: 'var(--app-text-muted)',
										marginTop: 3,
										paddingLeft: msg.role === 'user' ? 0 : 4,
										paddingRight: msg.role === 'user' ? 4 : 0,
									}}
								>
									{fmtTime(msg.time)}
								</div>
							</div>
						))}

						{/* Typing indicator */}
						{loading && (
							<div style={{ alignSelf: 'flex-start', maxWidth: '84%' }}>
								<div
									style={{
										background: 'var(--app-card-bg)',
										borderRadius: '1.1rem 1.1rem 1.1rem 0.2rem',
										padding: '0.55rem 0.85rem',
										boxShadow: '0 1px 5px rgba(0,0,0,.08)',
									}}
								>
									<TypingDots />
								</div>
							</div>
						)}

						{/* Retry button on error */}
						{lastError === 'generic' && !loading && (
							<div style={{ alignSelf: 'flex-start' }}>
								<button
									onClick={retryLast}
									style={{
										background: 'none',
										border: '1px solid var(--app-border)',
										borderRadius: '1rem',
										padding: '0.3rem 0.8rem',
										fontSize: '0.75rem',
										cursor: 'pointer',
										color: 'var(--app-text-muted)',
										display: 'flex',
										alignItems: 'center',
										gap: 5,
									}}
								>
									<ArrowClockwiseIcon weight="bold" size={16} />
									Try again
								</button>
							</div>
						)}

						<div ref={endRef} />
					</div>

					{/* Suggested questions */}
					{showSuggestions && (
						<div
							style={{
								padding: '0.55rem 0.75rem',
								background: 'var(--app-bg)',
								display: 'flex',
								flexWrap: 'wrap',
								gap: '0.35rem',
								borderTop: '1px solid var(--app-border)',
								flexShrink: 0,
							}}
						>
							{SUGGESTED_QUESTIONS.map((q) => (
								<button
									key={q}
									className="cb-suggest-btn"
									onClick={() => sendMessage(q)}
									disabled={loading}
									style={{
										background: 'var(--app-card-bg)',
										border: '1px solid var(--app-border)',
										borderRadius: '1rem',
										padding: '0.28rem 0.65rem',
										fontSize: '0.73rem',
										cursor: loading ? 'not-allowed' : 'pointer',
										color: 'var(--app-text)',
										transition: 'all 0.15s',
										opacity: loading ? 0.6 : 1,
									}}
								>
									{q}
								</button>
							))}
						</div>
					)}

					{/* Input area */}
					<div
						style={{
							padding: '0.7rem 0.85rem',
							borderTop: '1px solid var(--app-border)',
							background: 'var(--app-card-bg)',
							flexShrink: 0,
						}}
					>
						<CInputGroup size="sm">
							<CFormInput
								ref={inputRef}
								placeholder="Ask a security question…"
								value={input}
								onChange={(e) => setInput(e.target.value)}
								onKeyDown={handleKeyDown}
								disabled={loading}
								maxLength={MAX_CHARS + 50}
								style={{ borderRadius: '0.5rem 0 0 0.5rem', fontSize: '0.875rem' }}
							/>
							<CInputGroupText
								title={loading ? 'Waiting…' : 'Send'}
								onClick={() => !loading && !overLimit && sendMessage()}
								style={{
									cursor: loading || !input.trim() || overLimit ? 'not-allowed' : 'pointer',
									background:
										!input.trim() || overLimit
											? 'var(--app-border)'
											: BRAND,
									color: !input.trim() || overLimit ? 'var(--app-text-muted)' : '#fff',
									border: 'none',
									borderRadius: '0 0.5rem 0.5rem 0',
									transition: 'background 0.15s',
									padding: '0 0.75rem',
								}}
							>
								<PaperPlaneTiltIcon weight="bold" size={16} />
							</CInputGroupText>
						</CInputGroup>
						{overLimit && (
							<div style={{ fontSize: '0.7rem', color: '#dc3545', marginTop: 3 }}>
								{charCount}/{MAX_CHARS} — message too long
							</div>
						)}
						{!overLimit && charCount > MAX_CHARS * 0.8 && (
							<div style={{ fontSize: '0.7rem', color: 'var(--app-text-muted)', marginTop: 3, textAlign: 'right' }}>
								{charCount}/{MAX_CHARS}
							</div>
						)}
					</div>
				</div>
			)}
		</>
	);
};

export default Chatbot;
